/**
 * /admin/$slug/roster — owner/admin roster overview.
 * ADR-0014 V2 Phase 4.
 *
 * Staff × dates grid. Columns = next 14 days by default, navigable
 * forward/back by weeks. Rows = every active member (staff/admin/owner)
 * prefixed with their staff_type icon so the cost waterfall is visible
 * at a glance.
 *
 * Cells encode:
 *   ✓ working — rule covers this DoW
 *   — closed — no rule covers this DoW
 *   🌴 on leave — approved override, type 'time_off'
 *   🤒 sick — approved override, type 'sick'
 *   ➕ extra — approved override, type 'extra'
 *   🟡 pending — override exists, status 'pending'
 *   🔴 (decorator) — leave/sick cell with confirmed booking attached
 *
 * Owner/admin actions:
 *   - click empty cell → quick-add override (reuses my-availability dialog)
 *   - click staff name → /my-availability?userId=<id>
 *   - per-row "Confirmed through" → date picker writing
 *     tenant_memberships.availability_confirmed_through
 *   - approve/reject buttons in tooltip for pending overrides (only
 *     surfaces when tenant has require_time_off_approval)
 *
 * Regular staff role gets redirected to /my-availability — they don't
 * need the team-wide view.
 */
import {
  Link,
  createFileRoute,
  useNavigate,
} from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@bookaburra/ui';
import {
  AlertTriangle,
  CalendarRange,
  Check,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import {
  AvailabilityApiError,
  type AvailabilityOverride,
  type AvailabilityRule,
  type AvailabilityStaff,
  type StaffType,
  decideOverride,
  fetchAvailability,
  setConfirmedThrough,
} from '../booking-flow/availability-api';
import { fetchMyMemberships } from '../booking-flow/tenant-api';
import { fetchTenantSettings } from '../booking-flow/tenant-settings-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { fetchAdminCalendar } from '../booking-flow/admin-api';
import { useDocumentTitle } from '../lib/use-document-title';
import { AddOverrideDialog, localDateInTz } from './admin.$slug.my-availability';

export const Route = createFileRoute('/admin/$slug/roster')({
  component: RosterPage,
  validateSearch: (
    s: Record<string, unknown>,
  ): { from?: string } => ({
    from: typeof s.from === 'string' ? s.from : undefined,
  }),
});

const STAFF_TYPE_ICON: Record<StaffType, string> = {
  full_time: '\u{1F4BC}', // 💼
  part_time: '\u{1F553}', // 🕓
  paid_per_service: '\u{1F527}', // 🔧
  paid_hourly: '\u{23F1}', // ⏱
};

const STAFF_TYPE_LABEL: Record<StaffType, string> = {
  full_time: 'Full time',
  part_time: 'Part time',
  paid_per_service: 'Paid per service',
  paid_hourly: 'Paid hourly',
};

const DAYS_PER_VIEW = 14;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function RosterPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();

  const meQ = useQuery({
    queryKey: ['me-memberships'],
    queryFn: fetchMyMemberships,
    staleTime: 60_000,
  });
  const currentRole = meQ.data?.memberships.find((m) => m.tenant.slug === slug)?.role;
  const isAdmin = currentRole === 'owner' || currentRole === 'manager';
  const isOwner = currentRole === 'owner';

  // Staff get bounced to my-availability. The server also enforces but
  // a client redirect avoids the empty-state flash.
  useEffect(() => {
    if (currentRole === 'staff') {
      navigate({ to: '/admin/$slug/my-availability', params: { slug } });
    }
  }, [currentRole, navigate, slug]);

  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  const settingsQ = useQuery({
    queryKey: ['admin-tenant', slug],
    queryFn: () => fetchTenantSettings(slug),
    enabled: isAdmin,
  });
  const availQ = useQuery({
    queryKey: ['availability', slug],
    queryFn: () => fetchAvailability(slug),
    enabled: isAdmin,
  });

  useDocumentTitle(
    tenantQ.data ? `Roster · ${tenantQ.data.tenant.name} Admin` : 'Roster',
  );

  const timezone = tenantQ.data?.tenant.timezone ?? 'Australia/Sydney';
  const requireApproval = settingsQ.data?.tenant.requireTimeOffApproval ?? false;

  const today = useMemo(() => localDateInTz(Date.now(), timezone), [timezone]);
  const [anchorDate, setAnchorDate] = useState<string>(today);
  useEffect(() => {
    if (anchorDate < today) setAnchorDate(today);
  }, [today, anchorDate]);

  // Range = anchorDate + 14 days. The calendar query needs the epoch
  // bounds in the tenant's timezone.
  const rangeStartDate = anchorDate;
  const rangeEndDate = useMemo(
    () => addDays(anchorDate, DAYS_PER_VIEW - 1),
    [anchorDate],
  );

  // Calendar bookings for the visible window — drives the 🔴 marker
  // ("staff has an active leave AND a confirmed booking that day").
  // The endpoint takes epoch ms bounds, so compute midnight at the
  // range edges in the tenant's timezone.
  const rangeStartMs = useMemo(
    () => epochAtMidnight(rangeStartDate, timezone),
    [rangeStartDate, timezone],
  );
  const rangeEndMs = useMemo(
    () => epochAtMidnight(rangeEndDate, timezone) + 24 * 60 * 60 * 1000,
    [rangeEndDate, timezone],
  );
  const calendarQ = useQuery({
    queryKey: ['roster-calendar', slug, rangeStartMs, rangeEndMs],
    queryFn: () => fetchAdminCalendar(slug, rangeStartMs, rangeEndMs),
    enabled: isAdmin,
    staleTime: 30_000,
  });

  const days = useMemo(
    () => buildDayList(anchorDate, DAYS_PER_VIEW),
    [anchorDate],
  );

  if (meQ.isLoading || !currentRole) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-10 sm:px-12">
        <div className="h-64 animate-pulse rounded-2xl bg-secondary" />
      </main>
    );
  }
  if (!isAdmin) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-10 sm:px-12">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Not allowed</AlertTitle>
          <AlertDescription>
            Only owners and admins see the roster overview. Try{' '}
            <Link
              to="/admin/$slug/my-availability"
              params={{ slug }}
              className="underline"
            >
              My availability
            </Link>{' '}
            instead.
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  if (availQ.isLoading) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-10 sm:px-12">
        <div className="h-64 animate-pulse rounded-2xl bg-secondary" />
      </main>
    );
  }

  const availability = availQ.data;
  if (!availability) return null;

  // Build per-staff per-date cell summaries up-front. O(staff × days)
  // with cheap lookups inside.
  const grid = buildGrid({
    staff: availability.staff,
    rules: availability.rules,
    overrides: availability.overrides,
    days,
    timezone,
    bookings:
      calendarQ.data?.bookings.map((b) => ({
        id: b.id,
        start: b.rangeStart,
        end: b.rangeEnd,
        staffIds: b.staffIds,
      })) ?? [],
  });

  return (
    <main className="mx-auto max-w-7xl px-6 py-10 sm:px-12">
      <header className="mb-6">
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
          <CalendarRange className="h-7 w-7 text-muted-foreground" />
          <span>Roster</span>
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Two-week view of who's working and who's off. Click a cell to add
          a one-off. Click a name to edit their working pattern.
        </p>
      </header>

      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setAnchorDate((d) => addDays(d, -7))}
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setAnchorDate(today)}
          >
            Today
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setAnchorDate((d) => addDays(d, 7))}
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="ml-2 text-sm text-muted-foreground">
            {formatDateLabel(rangeStartDate, timezone)} —{' '}
            {formatDateLabel(rangeEndDate, timezone)}
          </span>
        </div>
        <LegendChips />
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 min-w-[220px] border-b border-r border-brand-sage/15 bg-background px-3 py-2 text-left">
                Staff
              </th>
              {days.map((d) => (
                <th
                  key={d}
                  className="border-b border-brand-sage/15 px-2 py-2 text-center text-xs font-medium text-muted-foreground"
                  style={{ minWidth: 64 }}
                >
                  {formatDayHeader(d, timezone)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row) => (
              <RosterRow
                key={row.staff.id}
                slug={slug}
                row={row}
                days={days}
                timezone={timezone}
                requireApproval={requireApproval}
                isOwner={isOwner}
              />
            ))}
          </tbody>
        </table>
      </Card>
    </main>
  );
}

function LegendChips() {
  const items: Array<{ icon: string; label: string }> = [
    { icon: '✓', label: 'Working' },
    { icon: '—', label: 'Closed' },
    { icon: '\u{1F334}', label: 'Time off' },
    { icon: '\u{1F912}', label: 'Sick' },
    { icon: '+', label: 'Extra' },
    { icon: '⚠', label: 'Pending' },
    { icon: '⚫', label: 'Booking clash' },
  ];
  return (
    <div className="flex flex-wrap gap-1 text-[11px] text-muted-foreground">
      {items.map((it) => (
        <span
          key={it.label}
          className="inline-flex items-center gap-1 rounded-full border border-brand-sage/20 bg-brand-cream/40 px-2 py-0.5"
        >
          <span aria-hidden>{it.icon}</span>
          {it.label}
        </span>
      ))}
    </div>
  );
}

interface GridRow {
  staff: AvailabilityStaff;
  cells: CellState[];
}

interface CellState {
  date: string;
  working: { open: number; close: number } | null;
  override:
    | (Pick<AvailabilityOverride, 'id' | 'type' | 'status' | 'note'> & {
        rangeStart: number;
        rangeEnd: number;
      })
    | null;
  bookingClash: boolean;
}

function buildGrid({
  staff,
  rules,
  overrides,
  days,
  timezone,
  bookings,
}: {
  staff: AvailabilityStaff[];
  rules: AvailabilityRule[];
  overrides: AvailabilityOverride[];
  days: string[];
  timezone: string;
  bookings: Array<{ id: string; start: number; end: number; staffIds: string[] }>;
}): { rows: GridRow[] } {
  const rulesByStaff = new Map<string, AvailabilityRule[]>();
  for (const r of rules) {
    const list = rulesByStaff.get(r.staffId) ?? [];
    list.push(r);
    rulesByStaff.set(r.staffId, list);
  }
  const overridesByStaff = new Map<string, AvailabilityOverride[]>();
  for (const o of overrides) {
    const list = overridesByStaff.get(o.staffId) ?? [];
    list.push(o);
    overridesByStaff.set(o.staffId, list);
  }

  const rows: GridRow[] = staff.map((s) => {
    const staffRules = rulesByStaff.get(s.id) ?? [];
    const staffOverrides = overridesByStaff.get(s.id) ?? [];
    const staffBookings = bookings.filter((b) => b.staffIds.includes(s.id));
    const cells: CellState[] = days.map((d) => {
      const isoDay = isoDayOfDate(d);
      const mask = 1 << (isoDay - 1);
      const rule = staffRules.find((r) => (r.daysOfWeekMask & mask) !== 0);
      const working = rule
        ? { open: rule.startMinuteOfDay, close: rule.endMinuteOfDay }
        : null;

      const dayStart = epochAtMidnight(d, timezone);
      const dayEnd = dayStart + 24 * 60 * 60 * 1000;
      // Find an override that overlaps this calendar day (any status).
      const override = staffOverrides.find(
        (o) => o.rangeStart < dayEnd && o.rangeEnd > dayStart,
      );
      const bookingClash =
        !!override &&
        override.status === 'approved' &&
        (override.type === 'time_off' || override.type === 'sick') &&
        staffBookings.some((b) => b.start < dayEnd && b.end > dayStart);

      return {
        date: d,
        working,
        override: override
          ? {
              id: override.id,
              type: override.type,
              status: override.status,
              note: override.note,
              rangeStart: override.rangeStart,
              rangeEnd: override.rangeEnd,
            }
          : null,
        bookingClash,
      };
    });
    return { staff: s, cells };
  });

  return { rows };
}

function RosterRow({
  slug,
  row,
  days,
  timezone,
  requireApproval,
  isOwner,
}: {
  slug: string;
  row: GridRow;
  days: string[];
  timezone: string;
  requireApproval: boolean;
  isOwner: boolean;
}) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<{ date: string } | null>(null);
  const [confirmedOpen, setConfirmedOpen] = useState(false);

  const confirmedThrough = row.staff.availabilityConfirmedThrough;
  const confirmedLabel = confirmedThrough
    ? `Confirmed thru ${formatDateLabel(
        localDateInTz(confirmedThrough, timezone),
        timezone,
      )}`
    : 'Confirm availability';

  return (
    <tr className="hover:bg-brand-cream/30">
      <th
        scope="row"
        className="sticky left-0 z-[1] border-b border-r border-brand-sage/15 bg-background px-3 py-2 text-left align-top"
      >
        <Link
          to="/admin/$slug/my-availability"
          params={{ slug }}
          search={{ userId: row.staff.id }}
          className="block hover:underline"
        >
          <span className="inline-flex items-center gap-1.5 text-sm font-medium">
            <span aria-hidden title={STAFF_TYPE_LABEL[row.staff.staffType]}>
              {STAFF_TYPE_ICON[row.staff.staffType]}
            </span>
            {row.staff.name}
          </span>
        </Link>
        <div className="mt-1 text-[11px] text-muted-foreground">
          {STAFF_TYPE_LABEL[row.staff.staffType]} · {row.staff.role}
        </div>
        {isOwner ? (
          <button
            type="button"
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-brand-sage hover:text-brand-ink-soft"
            onClick={() => setConfirmedOpen(true)}
          >
            <Check className="h-3 w-3" /> {confirmedLabel}
          </button>
        ) : null}
      </th>
      {row.cells.map((cell) => (
        <RosterCell
          key={cell.date}
          slug={slug}
          cell={cell}
          timezone={timezone}
          requireApproval={requireApproval}
          isOwner={isOwner}
          onAdd={() => setDialog({ date: cell.date })}
        />
      ))}
      {dialog ? (
        <td className="hidden">
          {/* Dialog is rendered outside the table flow but inside the
              row so the navigate-back state survives. */}
          <AddOverrideDialog
            slug={slug}
            staffId={row.staff.id}
            staffName={row.staff.name}
            timezone={timezone}
            willPending={false /* owner/admin actions auto-approve */}
            initialDate={dialog.date}
            onClose={() => setDialog(null)}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ['availability', slug] });
              setDialog(null);
            }}
          />
        </td>
      ) : null}
      {confirmedOpen ? (
        <td className="hidden">
          <ConfirmedThroughDialog
            slug={slug}
            staff={row.staff}
            timezone={timezone}
            onClose={() => setConfirmedOpen(false)}
          />
        </td>
      ) : null}
    </tr>
  );
}

function RosterCell({
  slug,
  cell,
  timezone,
  requireApproval,
  isOwner,
  onAdd,
}: {
  slug: string;
  cell: CellState;
  timezone: string;
  requireApproval: boolean;
  isOwner: boolean;
  onAdd: () => void;
}) {
  const qc = useQueryClient();
  const decideMut = useMutation({
    mutationFn: ({ decision }: { decision: 'approve' | 'reject' }) =>
      decideOverride(slug, cell.override!.id, decision),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['availability', slug] }),
  });

  const display = cellDisplay(cell);
  const titleText = cellTooltip(cell, timezone);

  return (
    <td
      className="relative border-b border-brand-sage/10 p-1 text-center align-middle"
      title={titleText}
    >
      <button
        type="button"
        onClick={onAdd}
        className={`group relative flex h-10 w-full items-center justify-center rounded-md text-sm transition ${display.className}`}
      >
        <span aria-hidden className="text-base leading-none">
          {display.icon}
        </span>
        {cell.bookingClash ? (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-background"
            title="Booking already on the calendar for this date"
          />
        ) : null}
      </button>
      {cell.override?.status === 'pending' && requireApproval && isOwner ? (
        <div className="mt-1 flex items-center justify-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-5 px-1 text-[10px]"
            onClick={() => decideMut.mutate({ decision: 'approve' })}
            disabled={decideMut.isPending}
          >
            <Check className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-5 px-1 text-[10px]"
            onClick={() => decideMut.mutate({ decision: 'reject' })}
            disabled={decideMut.isPending}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : null}
    </td>
  );
}

function cellDisplay(cell: CellState): { icon: string; className: string } {
  if (cell.override) {
    if (cell.override.status === 'pending') {
      return {
        icon: '⚠', // ⚠ pending
        className: 'bg-yellow-50 text-yellow-900 hover:bg-yellow-100',
      };
    }
    if (cell.override.status === 'rejected') {
      // Rejected rows stay in audit but show as if it's just a normal
      // working/closed day — the override no longer affects allocator.
      return cell.working
        ? { icon: '✓', className: 'text-brand-sage hover:bg-brand-cream' }
        : { icon: '—', className: 'text-muted-foreground hover:bg-brand-cream' };
    }
    // Approved override.
    if (cell.override.type === 'time_off') {
      return {
        icon: '\u{1F334}', // 🌴
        className: 'bg-emerald-50 text-emerald-900 hover:bg-emerald-100',
      };
    }
    if (cell.override.type === 'sick') {
      return {
        icon: '\u{1F912}', // 🤒
        className: 'bg-amber-50 text-amber-900 hover:bg-amber-100',
      };
    }
    // extra
    return {
      icon: '+',
      className: 'bg-blue-50 text-blue-900 hover:bg-blue-100',
    };
  }
  if (cell.working) {
    return { icon: '✓', className: 'text-brand-sage hover:bg-brand-cream' };
  }
  return {
    icon: '—',
    className: 'text-muted-foreground/60 hover:bg-brand-cream',
  };
}

function cellTooltip(cell: CellState, timezone: string): string {
  const dateLabel = formatDateLabel(cell.date, timezone);
  if (cell.override) {
    const typeLabel =
      cell.override.type === 'time_off'
        ? 'Time off'
        : cell.override.type === 'sick'
          ? 'Sick'
          : 'Extra hours';
    const statusLabel =
      cell.override.status === 'pending'
        ? ' (pending approval)'
        : cell.override.status === 'rejected'
          ? ' (rejected)'
          : '';
    return `${dateLabel}: ${typeLabel}${statusLabel}${
      cell.override.note ? ` — ${cell.override.note}` : ''
    }${cell.bookingClash ? ' — booking on calendar; needs attention' : ''}`;
  }
  if (cell.working) {
    return `${dateLabel}: working ${toTimeString(cell.working.open)}–${toTimeString(
      cell.working.close,
    )}`;
  }
  return `${dateLabel}: closed`;
}

function ConfirmedThroughDialog({
  slug,
  staff,
  timezone,
  onClose,
}: {
  slug: string;
  staff: AvailabilityStaff;
  timezone: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const existing = staff.availabilityConfirmedThrough;
  const initialDate = existing
    ? localDateInTz(existing, timezone)
    : localDateInTz(Date.now() + 7 * MS_PER_DAY, timezone);
  const [date, setDate] = useState<string>(initialDate);

  const saveMut = useMutation({
    mutationFn: (next: string | null) => setConfirmedThrough(slug, staff.id, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['availability', slug] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark availability confirmed</DialogTitle>
          <DialogDescription>
            You've checked with {staff.name} and their availability through
            this date is real. Soft signal — doesn't change allocator
            behaviour, but suppresses sweep warnings up to the date.
          </DialogDescription>
        </DialogHeader>
        <FormField label="Confirmed through">
          {({ id }) => (
            <Input
              id={id}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          )}
        </FormField>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => saveMut.mutate(null)}
            disabled={saveMut.isPending || existing === null}
          >
            Clear
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => saveMut.mutate(date)}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------ helpers --------------------------------- */

function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function buildDayList(anchor: string, count: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) out.push(addDays(anchor, i));
  return out;
}

function isoDayOfDate(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  // getUTCDay: Sun=0..Sat=6 → ISO Mon=1..Sun=7.
  const dow = dt.getUTCDay();
  return dow === 0 ? 7 : dow;
}

function epochAtMidnight(date: string, timezone: string): number {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return Date.now();
  let candidate = Date.UTC(y, m - 1, d, 0, 0, 0, 0);
  for (let i = 0; i < 2; i++) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date(candidate));
    const yy = Number(parts.find((p) => p.type === 'year')?.value ?? 0);
    const mm = Number(parts.find((p) => p.type === 'month')?.value ?? 0);
    const dd = Number(parts.find((p) => p.type === 'day')?.value ?? 0);
    const hh = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
    const mi = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
    const wantMs = Date.UTC(y, m - 1, d, 0, 0, 0, 0);
    const haveMs = Date.UTC(yy, mm - 1, dd, hh % 24, mi, 0, 0);
    const delta = wantMs - haveMs;
    if (delta === 0) return candidate;
    candidate += delta;
  }
  return candidate;
}

function formatDateLabel(date: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    day: 'numeric',
    month: 'short',
  }).format(new Date(epochAtMidnight(date, timezone)));
}

function formatDayHeader(date: string, timezone: string): string {
  const dt = new Date(epochAtMidnight(date, timezone));
  const weekday = new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    weekday: 'short',
  }).format(dt);
  const day = new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    day: 'numeric',
  }).format(dt);
  return `${weekday} ${day}`;
}

function toTimeString(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
